import React, { useEffect, useRef } from "react";

function VoiceWaveform({ active, muted, paused }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const ampRef = useRef(0);
  const phaseRef = useRef(0);
  const mutedRef = useRef(muted);
  const pausedRef = useRef(paused);

  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  useEffect(() => {
    if (!active) return undefined;

    let cancelled = false;
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const setupAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const AC = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AC();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.85;
        source.connect(analyser);
        audioCtxRef.current = audioCtx;
        analyserRef.current = analyser;
      } catch (e) {
        analyserRef.current = null;
      }
    };

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const ctx = canvas.getContext("2d");
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const targetW = Math.max(1, Math.floor(rect.width * dpr));
      const targetH = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
      }
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      let level = 0;
      if (analyserRef.current && !mutedRef.current && !pausedRef.current) {
        const buf = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        level = Math.sqrt(sum / buf.length);
      }

      ampRef.current = ampRef.current * 0.82 + level * 0.18;
      const amp = Math.min(1, ampRef.current * 3.2);

      if (!prefersReduced) phaseRef.current += 0.022;
      const phase = phaseRef.current;

      const midY = h / 2;
      const idleAmp = h * 0.08;
      const activeAmp = h * 0.36 * amp;
      const baseAmp = idleAmp + activeAmp;

      const layers = [
        { color: "rgba(16,185,129,0.65)", freq: 1.7, phaseOff: 0, amp: 1.0, width: 1.6 },
        { color: "rgba(59,130,246,0.45)", freq: 2.4, phaseOff: 1.1, amp: 0.72, width: 1.3 },
        { color: "rgba(255,255,255,0.20)", freq: 3.3, phaseOff: 2.2, amp: 0.50, width: 1.0 },
      ];

      const step = Math.max(1, Math.floor(dpr));
      layers.forEach((layer) => {
        ctx.beginPath();
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, "rgba(16,185,129,0)");
        grad.addColorStop(0.5, layer.color);
        grad.addColorStop(1, "rgba(59,130,246,0)");
        ctx.strokeStyle = grad;
        ctx.lineWidth = layer.width * dpr;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.shadowColor = layer.color;
        ctx.shadowBlur = 10 * dpr;

        for (let x = 0; x <= w; x += step) {
          const t = x / w;
          const env = Math.sin(Math.PI * t);
          const y =
            midY +
            Math.sin(t * Math.PI * 2 * layer.freq + phase + layer.phaseOff) *
              baseAmp *
              layer.amp *
              env;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      });
      ctx.shadowBlur = 0;

      rafRef.current = requestAnimationFrame(draw);
    };

    setupAudio();
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (e) { /* noop */ }
        audioCtxRef.current = null;
      }
      analyserRef.current = null;
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      className={`voice-waveform${paused ? " is-paused" : ""}${muted ? " is-muted" : ""}`}
      aria-hidden="true"
      role="presentation"
    >
      <canvas ref={canvasRef} className="voice-waveform__canvas" />
    </div>
  );
}

export default React.memo(VoiceWaveform);
